
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { X, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/context/LanguageContext';
import { useCustomLabels } from '@/hooks/useCustomLabels';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface NumberPadProps {
  title?: string;
  onExhibitFound?: (id: string) => void;
  className?: string;
}

const NumberPad: React.FC<NumberPadProps> = ({
  title,
  onExhibitFound,
  className = ""
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { getLabel } = useCustomLabels();
  
  const handleKeyPress = (value: string | number) => {
    console.log("Key pressed:", value);
    // Clear error alert when user starts typing
    if (showErrorAlert) {
      setShowErrorAlert(false);
    }
    
    if (value === 'clear') {
      setInputValue('');
    } else if (value === 'delete') {
      const newValue = inputValue.slice(0, -1);
      console.log("Delete pressed, new value:", newValue);
      setInputValue(newValue);
    } else if (value === 'go') {
      handleSubmit();
    } else {
      setInputValue(inputValue + value);
    }
  };
  
  const handleSubmit = async () => {
    if (!inputValue) return;
    
    setIsSearching(true);
    
    try {
      console.log("Searching for exhibit with number code:", inputValue);
      
      // Fix the query format - number_code should be passed as a string parameter
      // not using the eq.X format which causes 406 errors
      const { data, error } = await supabase
        .from('exhibits')
        .select('id')
        .eq('number_code', inputValue.trim())
        .maybeSingle();
      
      if (error) {
        console.error("Error searching for exhibit:", error);
        setAlertMessage(`${t('no_exhibit_found_with_number')} ${inputValue}`);
        setShowErrorAlert(true);
      } else if (data) {
        console.log("Exhibit found:", data.id);
        
        // Navigate immediately without toast
        if (onExhibitFound) {
          onExhibitFound(data.id);
        } else {
          // Use state to remember where we came from
          navigate(`/exhibit/${data.id}`, { 
            state: { from: 'keypad' } 
          });
        }
      } else {
        console.log("No exhibit found with code:", inputValue);
        setAlertMessage(`${t('no_exhibit_found_with_number')} ${inputValue}`);
        setShowErrorAlert(true);
      }
    } catch (error) {
      console.error("Exception in exhibit search:", error);
      setAlertMessage(t('unexpected_error'));
      setShowErrorAlert(true);
    } finally {
      setIsSearching(false);
      setInputValue('');
    }
  };

  // Auto-close alert after 2 seconds
  useEffect(() => {
    if (showErrorAlert) {
      const timer = setTimeout(() => {
        setShowErrorAlert(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [showErrorAlert]);
  
  return (
    <div className={`p-6 max-w-md mx-auto ${className}`}>
      
      {/* Display */}
      <div className="bg-white p-6 rounded-lg mb-6 text-center border border-border shadow-sm" dir="ltr">
        <span className={`text-3xl font-semibold tracking-wide transition-colors ${
          inputValue ? 'text-foreground' : 'text-muted-foreground'
        }`}>
          {inputValue || '___'}
        </span>
      </div>
      
      {/* Button Grid */}
      <div className="grid grid-cols-3 gap-3 mb-6" dir="ltr">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
          <Button 
            key={num}
            variant="outline"
            className="h-14 text-xl font-semibold bg-museum/5 border-museum/20 text-museum hover:bg-museum/10 hover:border-museum/30 transition-colors"
            onClick={() => handleKeyPress(num)}
          >
            {num}
          </Button>
        ))}
        
        <Button 
          variant="outline"
          className="h-14 font-semibold bg-destructive/5 border-destructive/20 text-destructive hover:bg-destructive/10 hover:border-destructive/30 transition-colors"
          onClick={() => handleKeyPress('clear')}
        >
          <X className="h-5 w-5" />
        </Button>
        
        <Button 
          variant="outline"
          className="h-14 text-xl font-semibold bg-museum/5 border-museum/20 text-museum hover:bg-museum/10 hover:border-museum/30 transition-colors"
          onClick={() => handleKeyPress(0)}
        >
          0
        </Button>
        
        <Button
          variant="outline"
          className="h-14 text-xl font-semibold bg-muted/50 border-muted-foreground/20 text-muted-foreground hover:bg-muted hover:border-muted-foreground/30 transition-colors"
          onClick={() => handleKeyPress('delete')}
          onTouchStart={(e) => {
            e.preventDefault();
            handleKeyPress('delete');
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
          }}
        >
          ←
        </Button>
      </div>
      
      {/* Submit Button */}
      <Button 
        className="w-full h-12 text-base font-semibold bg-museum text-white border-2 border-museum-dark hover:bg-museum-dark hover:border-museum transition-all disabled:opacity-60 disabled:bg-museum/70 disabled:border-museum/50"
        disabled={inputValue.length === 0 || isSearching}
        onClick={handleSubmit}
      >
        <span className={isSearching ? 'animate-pulse' : ''}>
          {isSearching ? t('searching') : (title || getLabel('enter_number', 'enter_number_button'))}
        </span>
      </Button>

      {/* Error Alert Dialog */}
      <AlertDialog open={showErrorAlert} onOpenChange={setShowErrorAlert}>
        <AlertDialogContent className="max-w-sm mx-4 bg-white border-2 border-destructive/20 shadow-2xl">
          <div className="flex flex-col items-center space-y-4 p-2">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
              <AlertCircle className="w-6 h-6 text-destructive" />
            </div>
            <AlertDialogTitle className="text-destructive font-bold text-lg text-center">
              Exhibit Not Found
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-muted-foreground leading-relaxed">
              {alertMessage}
            </AlertDialogDescription>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NumberPad;
